import { z } from 'zod';

/**
 * SIL-808 — signals pipeline dashboard DTOs.
 *
 * Backs GET /api/signals-pipeline — one grouped-aggregate query per section,
 * driven entirely by `market_signal_series` / `market_signal_point`
 * (db/schema/790_market_signal.sql). Rows are never hardcoded per source: a
 * new adapter's series show up here the moment it upserts a
 * market_signal_series row, no code change required.
 *
 * Ops-only surface: relies on the same DB-role boundary as fmv-forecast.ts
 * (GRANT SELECT ON ALL TABLES ... TO ops_readonly, db/schema/450_ops_role.sql)
 * — not duplicated at the API layer.
 */

export const signalLicenseStatusSchema = z.enum(['permitted', 'pending_permission', 'internal_only']);
export type SignalLicenseStatus = z.infer<typeof signalLicenseStatusSchema>;

export const signalStalenessSchema = z.enum(['fresh', 'stale', 'never']);
export type SignalStaleness = z.infer<typeof signalStalenessSchema>;

export const signalSourceRowSchema = z.object({
  source: z.string(),
  seriesCount: z.number().int(),
  /** 'YYYY-MM-DD' of the most recent observation across this source's series, or null if it has never reported one. */
  lastObservedDate: z.string().nullable(),
  pointsLast7d: z.number().int(),
  /** Distinct license_status values present among this source's series — a source can carry more than one. */
  licenseStatuses: z.array(signalLicenseStatusSchema),
  staleness: signalStalenessSchema,
});
export type SignalSourceRow = z.infer<typeof signalSourceRowSchema>;

export const signalDailyVolumePointSchema = z.object({
  /** 'YYYY-MM-DD'. */
  day: z.string(),
  pointCount: z.number().int(),
});
export type SignalDailyVolumePoint = z.infer<typeof signalDailyVolumePointSchema>;

export const signalsPipelineSummarySchema = z.object({
  totalPointsToday: z.number().int(),
  sourcesFresh: z.number().int(),
  sourcesStale: z.number().int(),
  sourcesNever: z.number().int(),
  /** Series with license_status != 'permitted' (pending_permission + internal_only). */
  gatedSeriesCount: z.number().int(),
});
export type SignalsPipelineSummary = z.infer<typeof signalsPipelineSummarySchema>;

export const compInflowRowSchema = z.object({
  sourceId: z.string(),
  /**
   * null when either (a) market_comp.price_kind doesn't exist yet on this
   * deploy (SIL-800 not yet merged/migrated) or (b) it exists but this row
   * predates backfill. The UI collapses to a single "unspecified" bucket
   * per source when every row for that source is null.
   */
  priceKind: z.string().nullable(),
  count: z.number().int(),
});
export type CompInflowRow = z.infer<typeof compInflowRowSchema>;

export const signalsPipelineResponseSchema = z.object({
  summary: signalsPipelineSummarySchema,
  sources: z.array(signalSourceRowSchema),
  /** Daily total point volume across all series, last 30 days, oldest first. */
  dailyVolume: z.array(signalDailyVolumePointSchema),
  compInflow: z.object({
    /** false until SIL-800 lands market_comp.price_kind — drives the UI's graceful-degradation copy. */
    priceKindColumnAvailable: z.boolean(),
    /** Comp inflow by source (and price_kind, once available), last 30 days. */
    rows: z.array(compInflowRowSchema),
  }),
});
export type SignalsPipelineResponse = z.infer<typeof signalsPipelineResponseSchema>;
